import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ROUTE_ABO_OVERVIEW, ROUTE_STORE_PRODUCT_GROUP, ROUTE_STORE_PRODUCT_GROUP_ALL } from 'src/config/routes';
import {
    ROUTE_WELCOME_STORE_PRODUCT_ABO_GROUP,
    ROUTE_WELCOME_STORE_PRODUCT_GROUP_SELECT,
} from 'src/config/welcome-routes';
import { AppRoute } from 'src/core/AppRoute';
import { useAccount } from 'src/core/hooks/account';
import { useSiteDefinition } from 'src/core/provider/SiteProvider';
import ProductGroup, { ProductGroupSlug } from 'src/entities/product-group/ProductGroup';
import { getEntities } from 'src/store/entities/selectors';
import { EntityDescriptions } from 'src/store/entities/types';

interface StoreRoutes {
    routes: AppRoute[];

    filterBySlug: (...slugs: ProductGroupSlug[]) => AppRoute[];
}

interface UseStoreRoutes {
    variant?: 'normal' | 'welcome';
    withAllGrp?: boolean;
    withAboGrp?: boolean;
}

export function useStoreRoutes({ variant, withAllGrp, withAboGrp }: UseStoreRoutes = {}): StoreRoutes {
    const productGroups = useSelector(state => getEntities(state, EntityDescriptions.PRODUCT_GROUP));
    const account = useAccount();
    const site = useSiteDefinition();

    const routes = useMemo(() => {
        const base = variant === 'welcome' ? ROUTE_WELCOME_STORE_PRODUCT_GROUP_SELECT : ROUTE_STORE_PRODUCT_GROUP;
        const slugFilter = site.productGroupSlugs;

        const activeGroups = slugFilter
            ? productGroups.items.filter((grp: ProductGroup) => (slugFilter as readonly string[]).includes(grp.slug))
            : productGroups.items;

        const _routes = activeGroups.map((grp: ProductGroup) => {
            const route = base.interpolatedClone({ productGroupSlug: grp.slug });
            route.slug = grp.slug;
            route.t_params = { productGroup: grp.localizedName };
            route.image = grp.imageUrl;
            route.thumbnail = grp.imageUrl;

            return route;
        });

        if (withAllGrp)
            _routes.unshift(
                variant === 'welcome'
                    ? base.interpolatedClone({ productGroupSlug: 'all' })
                    : ROUTE_STORE_PRODUCT_GROUP_ALL
            );

        if (withAboGrp)
            _routes.push(variant === 'welcome' ? ROUTE_WELCOME_STORE_PRODUCT_ABO_GROUP : ROUTE_ABO_OVERVIEW);

        return _routes.filter(
            route => route.accessPrivilege === undefined || account.hasPrivilege(route.accessPrivilege)
        );
    }, [variant, productGroups.items, withAllGrp, withAboGrp, account, site.productGroupSlugs]);

    const filterBySlug = useCallback(
        (...slugs: string[]): AppRoute[] => {
            return routes.filter(route => slugs.includes(route.slug));
        },
        [routes]
    );

    return {
        routes,
        filterBySlug,
    };
}
